import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { FileDown, XCircle, RefreshCw } from "lucide-react";
import { usePinPrompt } from "@/components/app/PinPrompt";

const downloadJson = (obj, name) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" }));
  const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
};
const Sel = ({ value, onChange, opts, tid }) => (
  <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
const MockNote = () => <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">Sample number generated by the app (MOCKED) — not registered with the GST portal. Download the JSON and upload it on the government portal, or connect a GSP to get real numbers.</div>;

export function EinvDialog({ open, onClose, t, data, onRefresh }) {
  const pinPrompt = usePinPrompt();
  const rec = data?.einvoice;
  const generate = async () => {
    await pinPrompt();
    try { await api.post(`/einvoice/${t._id_str}/generate`); toast.success("IRN generated"); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const cancel = async () => {
    const remarks = window.prompt("Reason for cancelling IRN (1 Duplicate, 2 Data entry mistake, 3 Order cancelled, 4 Others):", "Data entry mistake");
    if (remarks === null) return;
    await pinPrompt();
    try { await api.post(`/einvoice/${t._id_str}/cancel`, { reason_code: "2", remarks }); toast.success("IRN cancelled"); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="einv-dialog">
        <DialogHeader><DialogTitle>E-Invoice (GSTN IRN)</DialogTitle></DialogHeader>
        {data && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              {rec ? (
                <>
                  <div className="flex items-center gap-2"><span className={`chip ${rec.status === "active" ? "chip-green" : "chip-red"} uppercase`} data-testid="einv-status">{rec.status}</span></div>
                  <div><div className="overline">IRN</div><div className="font-mono text-xs break-all bg-slate-50 p-2 rounded border" data-testid="einv-irn">{rec.irn}</div></div>
                  <div className="text-sm font-mono">Ack {rec.ack_no} · {rec.ack_dt}</div>
                  <div className="text-center bg-white border border-border rounded p-3 inline-block"><QRCodeCanvas value={rec.qr_string} size={160} /></div>
                  {rec.status === "active" && <Button variant="outline" className="text-red-700" onClick={cancel} data-testid="einv-cancel"><XCircle size={14} /> Cancel IRN (within 24h)</Button>}
                </>
              ) : (
                <Button className="btn-primary" onClick={generate} data-testid="einv-generate">Generate IRN</Button>
              )}
              <Button variant="outline" onClick={() => downloadJson([data.payload], `einvoice-${t.number}.json`)} data-testid="einv-json"><FileDown size={14} /> Download JSON (portal upload)</Button>
              <MockNote />
            </div>
            <div><div className="overline">JSON Payload (GSTN schema v1.1)</div>
              <pre className="text-[10px] leading-relaxed bg-slate-900 text-emerald-200 p-3 rounded overflow-x-auto max-h-96">{JSON.stringify(data.payload, null, 2)}</pre></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const initForm = (t) => ({ distance_km: t.km || 0, trans_mode: "1", vehicle_no: t.vehicle_number || "", vehicle_type: "R",
  transporter_id: "", transporter_name: "", trans_doc_no: "", trans_doc_date: "", supply_type: "O", sub_supply_type: "1", from_pincode: "", to_pincode: "" });

function EwbForm({ t, onDone }) {
  const pinPrompt = usePinPrompt();
  const [f, setF] = useState(() => initForm(t));
  const F = (k, label, extra = {}) => <div><Label className="text-xs">{label}</Label><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: extra.type === "number" ? +e.target.value : e.target.value })} data-testid={`ewb-${k}`} {...extra} /></div>;
  const submit = async () => {
    await pinPrompt();
    try { await api.post(`/ewaybill/${t._id_str}/generate`, f); toast.success("E-way bill generated"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div><Label className="text-xs">Supply type</Label><Sel tid="ewb-supply_type" value={f.supply_type} onChange={(v) => setF({ ...f, supply_type: v })} opts={[["O", "Outward"], ["I", "Inward"]]} /></div>
        <div><Label className="text-xs">Sub type</Label><Sel tid="ewb-sub_supply_type" value={f.sub_supply_type} onChange={(v) => setF({ ...f, sub_supply_type: v })} opts={[["1", "Supply"], ["2", "Import"], ["3", "Export"], ["4", "Job Work"], ["5", "For Own Use"], ["8", "Others"]]} /></div>
        <div><Label className="text-xs">Mode</Label><Sel tid="ewb-trans_mode" value={f.trans_mode} onChange={(v) => setF({ ...f, trans_mode: v })} opts={[["1", "Road"], ["2", "Rail"], ["3", "Air"], ["4", "Ship"]]} /></div>
        {F("distance_km", "Distance (KM) *", { type: "number" })}
        {F("vehicle_no", "Vehicle No")}
        <div><Label className="text-xs">Vehicle type</Label><Sel tid="ewb-vehicle_type" value={f.vehicle_type} onChange={(v) => setF({ ...f, vehicle_type: v })} opts={[["R", "Regular"], ["O", "Over Dimensional Cargo"]]} /></div>
        {F("transporter_id", "Transporter ID (GSTIN)")}
        {F("transporter_name", "Transporter Name")}
        {F("trans_doc_no", "LR / RR / AWB No")}
        {F("trans_doc_date", "Transport Doc Date", { type: "date" })}
        {F("from_pincode", "From PIN")}
        {F("to_pincode", "To PIN")}
      </div>
      <Button className="btn-primary" onClick={submit} data-testid="ewb-generate">Generate E-Way Bill</Button>
    </div>
  );
}

function EwbActive({ t, rec, onDone }) {
  const pinPrompt = usePinPrompt();
  const partB = async () => {
    const vehicle_no = window.prompt("New vehicle number (Part-B update):", "");
    if (!vehicle_no) return;
    const remarks = window.prompt("Reason (1 Breakdown, 2 Transshipment, 3 Others):", "Transshipment");
    if (remarks === null) return;
    await pinPrompt();
    try { await api.post(`/ewaybill/${t._id_str}/part-b`, { vehicle_no, reason_code: "2", remarks }); toast.success("Vehicle updated"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const cancel = async () => {
    const remarks = window.prompt("Cancel reason (1 Duplicate, 2 Order cancelled, 3 Data entry mistake, 4 Others):", "Order cancelled");
    if (remarks === null) return;
    await pinPrompt();
    try { await api.post(`/ewaybill/${t._id_str}/cancel`, { reason_code: "2", remarks }); toast.success("E-way bill cancelled"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2"><span className={`chip ${rec.status === "active" ? "chip-green" : "chip-red"} uppercase`} data-testid="ewb-status">{rec.status}</span></div>
      <div className="font-mono text-lg font-bold" data-testid="ewb-number">{rec.ewb_no}</div>
      <div className="text-xs font-mono">Generated {new Date(rec.generated_at).toLocaleString()}<br />Valid upto {new Date(rec.valid_upto).toLocaleString()}</div>
      <div className="text-xs">Vehicle: <b className="font-mono">{rec.payload.vehicleNo || "—"}</b> · {rec.details.distance_km} KM · {rec.details.transporter_name || ""}</div>
      {(rec.part_b_history || []).length > 1 && <div className="text-[11px] text-slate-500">Part-B history: {rec.part_b_history.map((h) => h.vehicle_no).join(" → ")}</div>}
      <QRCodeCanvas value={`EWB:${rec.ewb_no}|VEH:${rec.payload.vehicleNo}|DT:${rec.generated_at}`} size={140} />
      {rec.status === "active" && (
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={partB} data-testid="ewb-partb"><RefreshCw size={14} /> Update Part-B (vehicle)</Button>
          <Button variant="outline" className="text-red-700" onClick={cancel} data-testid="ewb-cancel"><XCircle size={14} /> Cancel (within 24h)</Button>
        </div>
      )}
    </div>
  );
}

export function EwbDialog({ open, onClose, t, data, onRefresh }) {
  const rec = data?.ewaybill;
  const dlJson = async () => {
    try { const { data: j } = await api.get(`/ewaybill/${t._id_str}/json`); downloadJson(j, `ewaybill-${t.number}.json`); }
    catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ewb-dialog">
        <DialogHeader><DialogTitle>E-Way Bill — {t?.number}</DialogTitle></DialogHeader>
        {data && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              {rec && rec.status === "active" ? <EwbActive t={t} rec={rec} onDone={onRefresh} /> : (
                <>
                  {rec && <EwbActive t={t} rec={rec} onDone={onRefresh} />}
                  <EwbForm t={t} onDone={onRefresh} />
                </>
              )}
              <Button variant="outline" onClick={dlJson} data-testid="ewb-json"><FileDown size={14} /> Download JSON (NIC bulk upload)</Button>
              <MockNote />
            </div>
            <div><div className="overline">JSON Payload</div>
              <pre className="text-[10px] leading-relaxed bg-slate-900 text-emerald-200 p-3 rounded overflow-x-auto max-h-[28rem]">{JSON.stringify(rec?.payload || data.payload, null, 2)}</pre></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
